/**
 * Recompenses XP « gamifiees » partagees (player V2, warmup, demo, defi).
 *
 * Source de verite de l'XP reellement creditee = cote serveur (finish_attempt /
 * finishWarmup). Ce module ne sert QU'A l'affichage du feedback (popup centree
 * + bonus de serie + bonus de vitesse). Il est volontairement pur et
 * deterministe pour rester testable et independant du DOM — d'ou sa place dans
 * @jeraisonne/ui, partage entre apps/app (player, warmup, demo) et
 * apps/marketing (defi).
 *
 * Modele :
 *   - bonne reponse  -> `BASE_XP_PER_CORRECT` XP de base ;
 *   - serie (`streak`) de bonnes reponses d'affilee -> bonus croissant qui
 *     demarre a la 2e bonne reponse consecutive, plafonne pour eviter
 *     l'emballement ;
 *   - bonus de vitesse -> croit quand la bonne reponse arrive vite (voir
 *     `computeTimeBonus`), continu et borne ;
 *   - mauvaise reponse -> 0 XP, la serie retombe a 0.
 *
 * NB : l'echelle des XP a ete multipliee par 10 dans toute l'app (les nombres
 * affiches sont plus « gratifiants »). Le barème serveur (finish_attempt /
 * finish_warmup / complete_dag_attempt) suit la meme echelle et la meme courbe
 * de bonus de vitesse (reimplementee en plpgsql).
 */

/** XP de base accordee pour une bonne reponse (hors bonus de serie / vitesse). */
export const BASE_XP_PER_CORRECT = 100;

/** Palier de bonus ajoute par cran de serie au-dela de la 1re bonne reponse. */
export const STREAK_BONUS_STEP = 50;

/** Nombre de crans de bonus de serie maximum (anti-emballement). */
export const MAX_STREAK_BONUS_STEPS = 5;

/** Bonus de vitesse maximum (bonne reponse trouvee tres vite). */
export const TIME_BONUS_MAX = 50;

/** En dessous de ce temps de reponse (ms), le bonus de vitesse est plein. */
export const TIME_BONUS_FULL_MS = 3_000;

/** Au dela de ce temps de reponse (ms), le bonus de vitesse est nul. */
export const TIME_BONUS_ZERO_MS = 30_000;

/**
 * Bonus de vitesse (>= 0) accorde a une bonne reponse selon le temps mis pour
 * la trouver. Courbe LINEAIRE continue (pas de paliers) — d'ou « plus precis » :
 *   - `timeMs <= TIME_BONUS_FULL_MS` -> `TIME_BONUS_MAX` (plein) ;
 *   - `timeMs >= TIME_BONUS_ZERO_MS` -> 0 ;
 *   - entre les deux -> interpolation lineaire arrondie.
 *
 * `undefined`/`null` (temps inconnu) -> 0 (retro-compat des appelants sans temps).
 */
export function computeTimeBonus(timeMs?: number | null): number {
  if (timeMs == null || !Number.isFinite(timeMs)) return 0;
  const t = Math.max(0, timeMs);
  if (t <= TIME_BONUS_FULL_MS) return TIME_BONUS_MAX;
  if (t >= TIME_BONUS_ZERO_MS) return 0;
  const frac = (TIME_BONUS_ZERO_MS - t) / (TIME_BONUS_ZERO_MS - TIME_BONUS_FULL_MS);
  return Math.round(TIME_BONUS_MAX * frac);
}

export interface XpReward {
  /** XP de base (0 si mauvaise reponse). */
  base: number;
  /** Bonus de serie (0 si pas de serie active). */
  streakBonus: number;
  /** Bonus de vitesse (0 si temps inconnu / trop lent / mauvaise reponse). */
  timeBonus: number;
  /** XP total de cette reponse (`base + streakBonus + timeBonus`). */
  total: number;
  /** Longueur de la serie APRES cette reponse (0 si mauvaise reponse). */
  streak: number;
  /** Vrai des que le bonus de serie est actif (serie >= 2). */
  hasStreakBonus: boolean;
  /** Vrai des qu'un bonus de vitesse a ete accorde (> 0). */
  hasTimeBonus: boolean;
}

/**
 * Calcule la recompense d'une reponse a partir de la serie precedente et,
 * optionnellement, du temps mis pour repondre.
 *
 * @param wasCorrect reponse correcte ?
 * @param previousStreak longueur de la serie AVANT cette reponse (>= 0).
 * @param timeMs temps mis pour trouver la bonne reponse (ms). Omis -> pas de
 *   bonus de vitesse.
 */
export function computeXpReward(
  wasCorrect: boolean,
  previousStreak: number,
  timeMs?: number | null,
): XpReward {
  if (!wasCorrect) {
    return {
      base: 0,
      streakBonus: 0,
      timeBonus: 0,
      total: 0,
      streak: 0,
      hasStreakBonus: false,
      hasTimeBonus: false,
    };
  }
  const streak = Math.max(0, previousStreak) + 1;
  // Le bonus demarre a la 2e bonne reponse consecutive (streak - 1 crans),
  // plafonne a MAX_STREAK_BONUS_STEPS.
  const steps = Math.min(Math.max(streak - 1, 0), MAX_STREAK_BONUS_STEPS);
  const streakBonus = steps * STREAK_BONUS_STEP;
  const timeBonus = computeTimeBonus(timeMs);
  return {
    base: BASE_XP_PER_CORRECT,
    streakBonus,
    timeBonus,
    total: BASE_XP_PER_CORRECT + streakBonus + timeBonus,
    streak,
    hasStreakBonus: streak >= 2,
    hasTimeBonus: timeBonus > 0,
  };
}

/**
 * Libelle d'encouragement croissant selon la longueur de la serie.
 * `null` tant que la serie n'est pas « remarquable » (< 2).
 */
export function streakHeadline(streak: number): string | null {
  if (streak < 2) return null;
  if (streak < 3) return 'En forme !';
  if (streak < 5) return 'En feu !';
  if (streak < 8) return 'Imparable !';
  return 'Legendaire !';
}
